/**
 * `wp_mode` — what the WordPress plane is allowed to do with the rules it is served (ADR-0030).
 *
 * Deliberately **not** `edge_mode`. That field is meaningful only for a site proxied through our
 * edge — every reader gates on `edgeHostname` before looking at it — and its `bypass` state means
 * "traffic still flows through the Worker, unmodified", which is a statement about a proxy that is
 * not in this path. A site that is not on the edge has `edgeMode: undefined`, correctly, and that
 * must not also mean "the WordPress plane is off": most of the fleet is in exactly that state and is
 * precisely where a WP-plane engine matters.
 */

/**
 * - `enforce` — decisions are acted on.
 * - `monitor` — decisions are computed and **recorded**, never acted on.
 * - `off`     — the engine does not run.
 *
 * `off` is a third state rather than a tidier two because `monitor` still costs a rule evaluation on
 * every request, and an operator needs a way to make the plugin stop deciding without uninstalling
 * it — the answer to "is Guard Dog doing this?" during a support incident.
 */
export type WpMode = 'enforce' | 'monitor' | 'off';

export const WP_MODES: readonly WpMode[] = ['enforce', 'monitor', 'off'];

/**
 * The default, and where it lives: in the **absence** of a value.
 *
 * Unset column, absent wire field, unrecognised string, connector too old to know the field exists —
 * all resolve to `monitor`. Every site in the fleet has `wp_mode` unset, so "unset means monitor" is
 * what makes a live WP-plane engine observable instead of disruptive: rules are evaluated,
 * would-blocks are recorded, and nothing is denied until someone decides per site that it should be.
 *
 * Failing to `monitor` rather than to `off` is the deliberate second choice. `off` is safer and also
 * makes the rollout untestable — the fleet exists to produce the evidence that vpatch promotion
 * should have been measuring, and a plane that isn't deciding produces none (ADR-0030 §2).
 *
 * The PHP connector mirrors this function. It is three lines precisely so that mirroring it cannot
 * go wrong; anything cleverer belongs behind a conformance corpus.
 */
export function resolveWpMode(value: unknown): WpMode {
  return value === 'enforce' || value === 'off' ? value : 'monitor';
}

/**
 * Is this change an escalation, i.e. does an entitlement check apply?
 *
 * **Only arriving at `enforce` counts**, matching the edge's `movesTowardEnforce` exactly.
 *
 * The tempting implementation is a rank — `off(0) < monitor(1) < enforce(2)` — and it is wrong.
 * Under a rank, `off → monitor` is an escalation, so a lapsed tenant who turned a site off to answer
 * "is Guard Dog doing this?" during an incident could not turn it back on to `monitor`. Meanwhile
 * every one of that same tenant's untouched sites is *already* monitoring, because an unset column
 * resolves to `monitor` and there is no transition to gate. Two sites in one tenant would behave
 * differently based on history nobody can see, and the one that fails is the one somebody just
 * finished debugging.
 *
 * `monitor` is the floor, not a capability. It watches and records; it withholds nothing from any
 * visitor. Nothing about it is worth billing for, and gating it would mostly deny us the evidence
 * from the tenants whose configuration is already unusual (ADR-0030 §2).
 *
 * Moving away from enforcement is therefore never gated, and neither is arriving at the default.
 */
export function wpModeMovesTowardEnforce(from: WpMode | undefined, to: WpMode): boolean {
  return to === 'enforce' && resolveWpMode(from) !== 'enforce';
}
